import React, { useState, useEffect, useCallback } from 'react';
import { Modal, Button, Form } from 'react-bootstrap';
import { useAuth } from '../../context/AuthContext';
import config from '../../config';
import { toast } from 'react-toastify';

const ControlTowerTables = () => {
    const { auth } = useAuth();
    const [latestVenues, setLatestVenues] = useState([]);
    const [pendingVenues, setPendingVenues] = useState([]);
    const [loading, setLoading] = useState(true);

    // Action Modal State
    const [actionModal, setActionModal] = useState(false);
    const [actionType, setActionType] = useState(null); // 'approved' | 'rejected'
    const [actionReason, setActionReason] = useState('');
    const [selectedVenue, setSelectedVenue] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const fetchData = useCallback(async () => {
        try {
            const response = await auth.fetch(`${config.API_BASE_URL}/api/stats/control-tower`);
            if (response.ok) {
                const data = await response.json();
                setLatestVenues(data.latest_venues || []);
                setPendingVenues(data.pending_venues || []);
            }
        } catch (error) {
            console.error('Error fetching control tower tables:', error);
            toast.error('Failed to load table data');
        } finally {
            setLoading(false);
        }
    }, [auth]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const handleActionClick = (venue, type) => {
        setSelectedVenue(venue);
        setActionType(type);
        setActionReason('');
        setActionModal(true);
    };

    const submitAction = async () => {
        if (!selectedVenue || !actionType) return;
        
        setIsSubmitting(true);
        try {
            // Reusing the existing admin verification endpoint
            await auth.fetch(`${config.API_BASE_URL}/api/admin/venues/${selectedVenue.id}/verify`, {
                method: 'POST',
                body: JSON.stringify({
                    status: actionType,
                    notes: actionReason
                })
            });
            
            toast.success(`Venue ${actionType} successfully`);
            setActionModal(false);
            fetchData(); // Refresh data
        } catch (error) {
            console.error('Error performing action:', error);
            toast.error('Action failed');
        } finally {
            setIsSubmitting(false);
        }
    };

    const getStatusBadge = (status) => {
        switch(status) {
            case 'approved': return <span className="badge bg-success-focus text-success-main">Approved</span>;
            case 'rejected': return <span className="badge bg-danger-focus text-danger-main">Rejected</span>;
            case 'pending': return <span className="badge bg-warning-focus text-warning-main">Pending</span>;
            default: return <span className="badge bg-secondary-focus text-secondary-main">{status}</span>;
        }
    };

    return (
        <div className="card h-100">
            <div className="card-body p-24">
                <div className="d-flex flex-wrap align-items-center gap-1 justify-content-between mb-16">
                    <ul className="nav border-gradient-tab nav-pills mb-0" id="pills-tab" role="tablist">
                        <li className="nav-item" role="presentation">
                            <button className="nav-link d-flex align-items-center active" id="tab-venues" data-bs-toggle="pill" data-bs-target="#content-venues" type="button" role="tab">
                                Latest Venues
                            </button>
                        </li>
                        <li className="nav-item" role="presentation">
                            <button className="nav-link d-flex align-items-center" id="tab-reports" data-bs-toggle="pill" data-bs-target="#content-reports" type="button" role="tab">
                                Latest Reports
                            </button>
                        </li>
                        <li className="nav-item" role="presentation">
                            <button className="nav-link d-flex align-items-center" id="tab-approval" data-bs-toggle="pill" data-bs-target="#content-approval" type="button" role="tab">
                                Pending Approval
                                {pendingVenues.length > 0 && (
                                    <span className="text-sm fw-semibold py-6 px-12 bg-danger-600 rounded-pill text-white line-height-1 ms-12">
                                        {pendingVenues.length}
                                    </span>
                                )}
                            </button>
                        </li>
                    </ul>
                </div>

                <div className="tab-content" id="pills-tabContent">
                    {/* Latest Venues */}
                    <div className="tab-pane fade show active" id="content-venues" role="tabpanel" tabIndex={0}>
                        <div className="table-responsive scroll-sm">
                            <table className="table bordered-table sm-table mb-0">
                                <thead>
                                    <tr>
                                        <th scope="col">Business Name</th>
                                        <th scope="col">Owner</th>
                                        <th scope="col">Registered On</th>
                                        <th scope="col">Status</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {loading ? (
                                        <tr><td colSpan="4" className="text-center">Loading...</td></tr>
                                    ) : latestVenues.length === 0 ? (
                                        <tr><td colSpan="4" className="text-center">No venues found</td></tr>
                                    ) : (
                                        latestVenues.map(venue => (
                                            <tr key={venue.id}>
                                                <td className="fw-medium">{venue.business_name || venue.title}</td>
                                                <td>{venue.owner_name}</td>
                                                <td>{new Date(venue.created_at).toLocaleDateString()}</td>
                                                <td>{getStatusBadge(venue.verification_status)}</td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>

                    {/* Latest Reports */}
                    <div className="tab-pane fade" id="content-reports" role="tabpanel" tabIndex={0}>
                        <div className="table-responsive scroll-sm">
                             <table className="table bordered-table sm-table mb-0">
                                <thead>
                                    <tr>
                                        <th scope="col">Reported Item</th>
                                        <th scope="col">Reason</th>
                                        <th scope="col">Date</th>
                                        <th scope="col">Status</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {[1, 2].map(i => (
                                        <tr key={i}>
                                            <td className="fw-medium">Inappropriate Image</td>
                                            <td>Nudity</td>
                                            <td>2026-01-1{i}</td>
                                            <td><span className="badge bg-danger-focus text-danger-main">Open</span></td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>

                     {/* Pending Approval */}
                     <div className="tab-pane fade" id="content-approval" role="tabpanel" tabIndex={0}>
                        <div className="table-responsive scroll-sm">
                             <table className="table bordered-table sm-table mb-0">
                                <thead>
                                    <tr>
                                        <th scope="col">Venue</th>
                                        <th scope="col">Owner</th>
                                        <th scope="col">Submitted</th>
                                        <th scope="col">Action</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {loading ? (
                                        <tr><td colSpan="4" className="text-center">Loading...</td></tr>
                                    ) : pendingVenues.length === 0 ? (
                                        <tr><td colSpan="4" className="text-center">No pending venues</td></tr>
                                    ) : (
                                        pendingVenues.map(venue => (
                                            <tr key={venue.id}>
                                                <td className="fw-medium">{venue.business_name || venue.title}</td>
                                                <td>{venue.owner_name}</td>
                                                <td>{new Date(venue.created_at).toLocaleDateString()}</td>
                                                <td>
                                                    <button 
                                                        className="btn btn-sm btn-success-600 me-2"
                                                        onClick={() => handleActionClick(venue, 'approved')}
                                                    >
                                                        Approve
                                                    </button>
                                                    <button 
                                                        className="btn btn-sm btn-danger-600"
                                                        onClick={() => handleActionClick(venue, 'rejected')}
                                                    >
                                                        Reject
                                                    </button>
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>

                </div>
            </div>

            {/* Action Confirmation Modal */}
            <Modal show={actionModal} onHide={() => setActionModal(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Confirm {actionType === 'approved' ? 'Approval' : 'Rejection'}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p>Are you sure you want to {actionType === 'approved' ? 'approve' : 'reject'} <strong>{selectedVenue?.business_name || selectedVenue?.title}</strong>?</p>
                    <Form.Group className="mb-3">
                        <Form.Label>{actionType === 'rejected' ? 'Rejection Reason (Required)' : 'Notes (Optional)'}</Form.Label>
                        <Form.Control 
                            as="textarea" 
                            rows={3} 
                            value={actionReason} 
                            onChange={(e) => setActionReason(e.target.value)}
                            required={actionType === 'rejected'}
                        />
                    </Form.Group>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setActionModal(false)}>Cancel</Button>
                    <Button 
                        variant={actionType === 'approved' ? 'success' : 'danger'} 
                        onClick={submitAction}
                        disabled={isSubmitting || (actionType === 'rejected' && !actionReason.trim())}
                    >
                        {isSubmitting ? 'Processing...' : 'Confirm'}
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default ControlTowerTables;